import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format, addDays, startOfWeek, parseISO, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar, Clock, Plus, ChevronLeft, ChevronRight, X, Lock, 
  Settings, List, CalendarDays, User, Video, MoreVertical, Copy,
  Trash2, Edit2, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';

// Time slots from 07:00 to 21:00
const TIME_SLOTS = Array.from({ length: 29 }, (_, i) => {
  const hour = Math.floor(i / 2) + 7;
  const minutes = (i % 2) * 30;
  return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
});

export default function ProviderSchedules() {
  const { api } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'list'
  
  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Schedule config
  const [scheduleConfig, setScheduleConfig] = useState({
    startTime: '08:00',
    endTime: '18:00',
    slotDuration: 30,
    breakStart: '12:00',
    breakEnd: '13:00',
    hasBreak: true,
    workDays: [1, 2, 3, 4, 5], // Mon-Fri
  });

  useEffect(() => {
    fetchDoctors();
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedDoctor) {
      fetchSchedules();
      fetchAppointments();
    }
  }, [selectedDoctor, currentWeekStart]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/provider/doctors');
      setDoctors(response.data);
      if (response.data.length > 0) {
        setSelectedDoctor(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
      toast.error('Erro ao carregar médicos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const startDate = format(currentWeekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      const response = await api.get(`/provider/schedules?doctor_id=${selectedDoctor}&start_date=${startDate}&end_date=${endDate}`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const startDate = format(currentWeekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      const response = await api.get(`/provider/appointments?doctor_id=${selectedDoctor}&start_date=${startDate}&end_date=${endDate}`);
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const getWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(currentWeekStart, i));
    }
    return days;
  }, [currentWeekStart]);

  const getScheduleForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return schedules.find(s => s.date === dateStr);
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(a => a.date === dateStr);
  };

  const generateSlotsForDay = () => {
    const slots = [];
    let currentTime = scheduleConfig.startTime;
    const [startHour, startMin] = scheduleConfig.startTime.split(':').map(Number);
    const [endHour, endMin] = scheduleConfig.endTime.split(':').map(Number);
    const [breakStartHour, breakStartMin] = scheduleConfig.breakStart.split(':').map(Number);
    const [breakEndHour, breakEndMin] = scheduleConfig.breakEnd.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      
      // Skip break time
      const isBreak = scheduleConfig.hasBreak && (
        (currentHour > breakStartHour || (currentHour === breakStartHour && currentMin >= breakStartMin)) &&
        (currentHour < breakEndHour || (currentHour === breakEndHour && currentMin < breakEndMin))
      );

      if (!isBreak) {
        const nextMin = currentMin + scheduleConfig.slotDuration;
        const nextHour = currentHour + Math.floor(nextMin / 60);
        const endTimeStr = `${String(nextHour).padStart(2, '0')}:${String(nextMin % 60).padStart(2, '0')}`;
        
        slots.push({
          time: timeStr,
          end_time: endTimeStr,
          is_available: true,
          appointment_id: null
        });
      }

      currentMin += scheduleConfig.slotDuration;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    return slots;
  };

  const handleCreateWeekSchedule = async () => {
    if (!selectedDoctor) {
      toast.error('Selecione um médico');
      return;
    }

    try {
      const slots = generateSlotsForDay();
      
      // Create schedule for each work day
      for (const day of getWeekDays) {
        const dayOfWeek = day.getDay() === 0 ? 7 : day.getDay(); // Convert Sunday from 0 to 7
        
        if (scheduleConfig.workDays.includes(dayOfWeek)) {
          const dateStr = format(day, 'yyyy-MM-dd');
          
          // Check if schedule already exists
          const existing = schedules.find(s => s.date === dateStr);
          if (!existing) {
            await api.post('/provider/schedules', {
              doctor_id: selectedDoctor,
              date: dateStr,
              slots: slots.map(s => ({ ...s }))
            });
          }
        }
      }
      
      toast.success('Agenda da semana configurada com sucesso!');
      setConfigDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar agenda');
    }
  };

  const handleBlockDate = async () => {
    if (!selectedDate || !selectedDoctor) return;
    
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await api.post('/provider/schedules/block', {
        doctor_id: selectedDoctor,
        date: dateStr
      });
      toast.success('Data bloqueada com sucesso');
      setBlockDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error('Erro ao bloquear data');
    }
  };

  const handleCopyWeek = async () => {
    try {
      const nextWeekStart = addDays(currentWeekStart, 7);
      
      for (const schedule of schedules) {
        const scheduleDate = parseISO(schedule.date);
        const daysDiff = Math.floor((scheduleDate.getTime() - currentWeekStart.getTime()) / (1000 * 60 * 60 * 24));
        const newDate = addDays(nextWeekStart, daysDiff);
        const newDateStr = format(newDate, 'yyyy-MM-dd');
        
        await api.post('/provider/schedules', {
          doctor_id: selectedDoctor,
          date: newDateStr,
          slots: schedule.slots.map(s => ({ time: s.time, is_available: true, appointment_id: null }))
        });
      }
      
      toast.success('Agenda copiada para a próxima semana');
    } catch (error) {
      toast.error('Erro ao copiar agenda');
    }
  };

  const selectedDoctorData = doctors.find(d => d.id === selectedDoctor);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-schedules">
      <Navbar />

      <div className="container-app py-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/prestador" className="p-2 rounded-lg hover:bg-slate-100">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Agenda</h1>
              <p className="text-slate-600 text-sm">Gerencie os horários disponíveis</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Doctor Select */}
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="w-[220px] bg-white" data-testid="doctor-filter">
                <User className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Selecione o médico" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map((doctor) => (
                  <SelectItem key={doctor.id} value={doctor.id}>
                    {doctor.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex bg-white border border-slate-200 rounded-lg p-1">
              <Button 
                variant={viewMode === 'week' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('week')}
                className="rounded-md"
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                Semana
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-md"
              >
                <List className="w-4 h-4 mr-1" />
                Lista
              </Button>
            </div>

            {/* Actions */}
            <Button onClick={() => setConfigDialogOpen(true)} className="bg-primary">
              <Settings className="w-4 h-4 mr-2" />
              Configurar Horários
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setBlockDialogOpen(true)}>
                  <Lock className="w-4 h-4 mr-2" />
                  Bloquear Datas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCopyWeek}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar para Próxima Semana
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Hoje
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="font-medium text-slate-700 ml-2">
                {format(currentWeekStart, "dd 'de' MMMM", { locale: ptBR })} - {format(addDays(currentWeekStart, 6), "dd 'de' MMMM yyyy", { locale: ptBR })}
              </span>
            </div>
          </div>

          {/* Calendar Grid */}
          {viewMode === 'week' ? (
            <div className="overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Days Header */}
                <div className="grid grid-cols-8 border-b border-slate-100">
                  <div className="p-3 text-center text-sm font-medium text-slate-500 border-r border-slate-100">
                    Horário
                  </div>
                  {getWeekDays.map((day, index) => {
                    const isToday = isSameDay(day, new Date());
                    const schedule = getScheduleForDate(day);
                    const dayAppts = getAppointmentsForDate(day);
                    
                    return (
                      <div 
                        key={format(day, 'yyyy-MM-dd')} 
                        className={`p-3 text-center border-r border-slate-100 last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}
                      >
                        <p className="text-xs font-medium text-slate-500 uppercase">
                          {format(day, 'EEE', { locale: ptBR })}
                        </p>
                        <p className={`text-xl font-bold ${isToday ? 'text-primary' : 'text-slate-900'}`}>
                          {format(day, 'd')}
                        </p>
                        {schedule && (
                          <p className="text-xs text-emerald-600 mt-1">
                            {schedule.slots?.filter(s => s.is_available).length || 0} disponíveis
                          </p>
                        )}
                        {dayAppts.length > 0 && (
                          <p className="text-xs text-blue-600">
                            {dayAppts.length} agendados
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Time Slots Grid */}
                <div className="max-h-[600px] overflow-y-auto">
                  {TIME_SLOTS.filter((_, i) => i % 2 === 0).map((time, timeIndex) => (
                    <div key={time} className="grid grid-cols-8 border-b border-slate-50 hover:bg-slate-50/50">
                      <div className="p-2 text-xs text-slate-500 text-right pr-3 border-r border-slate-100">
                        {time}
                      </div>
                      {getWeekDays.map((day, dayIndex) => {
                        const schedule = getScheduleForDate(day);
                        const slot = schedule?.slots?.find(s => s.time === time);
                        const appt = appointments.find(a => 
                          a.date === format(day, 'yyyy-MM-dd') && a.time_slot === time
                        );
                        const isToday = isSameDay(day, new Date());

                        return (
                          <div 
                            key={format(day, 'yyyy-MM-dd')} 
                            className={`min-h-[60px] p-1 border-r border-slate-100 last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}
                          >
                            {appt ? (
                              <div className="bg-teal-100 border border-teal-200 rounded-lg p-2 text-xs h-full">
                                <p className="font-medium text-teal-900">{time}</p>
                                <p className="text-teal-700 truncate">{appt.patient_name || 'Paciente'}</p>
                                {appt.is_teleconsultation && (
                                  <Badge className="bg-blue-100 text-blue-700 text-[10px] mt-1">
                                    <Video className="w-3 h-3 mr-1" />
                                    Online
                                  </Badge>
                                )}
                              </div>
                            ) : slot?.is_available ? (
                              <div className="h-full border-l-2 border-emerald-400 bg-emerald-50/50 rounded-r px-2 py-1">
                                <p className="text-[10px] text-emerald-600">{time}</p>
                                <p className="text-[10px] text-emerald-500">Disponível</p>
                              </div>
                            ) : schedule?.is_blocked ? (
                              <div className="h-full bg-slate-100 rounded flex items-center justify-center">
                                <Lock className="w-3 h-3 text-slate-400" />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* List View */
            <div className="p-4">
              <div className="space-y-4">
                {getWeekDays.map((day, index) => {
                  const schedule = getScheduleForDate(day);
                  const dayAppts = getAppointmentsForDate(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div key={day.toISOString()} className={`p-4 rounded-xl border ${isToday ? 'border-primary bg-primary/5' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center ${isToday ? 'bg-primary text-white' : 'bg-slate-100'}`}>
                            <span className="text-xs uppercase">{format(day, 'EEE', { locale: ptBR })}</span>
                            <span className="text-lg font-bold">{format(day, 'd')}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{format(day, "EEEE, dd 'de' MMMM", { locale: ptBR })}</p>
                            <div className="flex gap-2 mt-1">
                              {schedule && (
                                <Badge variant="secondary" className="text-xs">
                                  {schedule.slots?.filter(s => s.is_available).length || 0} horários livres
                                </Badge>
                              )}
                              {dayAppts.length > 0 && (
                                <Badge className="bg-teal-100 text-teal-700 text-xs">
                                  {dayAppts.length} consultas
                                </Badge>
                              )}
                              {schedule?.is_blocked && (
                                <Badge variant="destructive" className="text-xs">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Bloqueado
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Appointments for this day */}
                      {dayAppts.length > 0 && (
                        <div className="grid gap-2 mt-3">
                          {dayAppts.map((appt) => (
                            <div key={appt.id || appt.time_slot} className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                              <div className="w-16 text-center">
                                <p className="font-bold text-teal-900">{appt.time_slot}</p>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-900">{appt.patient_name || 'Paciente'}</p>
                                <p className="text-sm text-slate-600">{appt.service || 'Consulta'}</p>
                              </div>
                              {appt.is_teleconsultation && (
                                <Badge className="bg-blue-100 text-blue-700">
                                  <Video className="w-3 h-3 mr-1" />
                                  Online
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-100 border-l-2 border-emerald-400 rounded-r"></div>
            <span className="text-slate-600">Disponível</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-teal-100 border border-teal-200 rounded"></div>
            <span className="text-slate-600">Agendado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-slate-200 rounded"></div>
            <span className="text-slate-600">Bloqueado</span>
          </div>
        </div>
      </div>

      {/* Configure Schedule Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurar Horários da Semana</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Início do Expediente</Label>
                <Select value={scheduleConfig.startTime} onValueChange={(v) => setScheduleConfig({...scheduleConfig, startTime: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.slice(0, 20).map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fim do Expediente</Label>
                <Select value={scheduleConfig.endTime} onValueChange={(v) => setScheduleConfig({...scheduleConfig, endTime: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.slice(10).map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Slot Duration */}
            <div className="space-y-2">
              <Label>Duração da Consulta (minutos)</Label>
              <Select value={String(scheduleConfig.slotDuration)} onValueChange={(v) => setScheduleConfig({...scheduleConfig, slotDuration: parseInt(v)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutos</SelectItem>
                  <SelectItem value="20">20 minutos</SelectItem>
                  <SelectItem value="30">30 minutos</SelectItem>
                  <SelectItem value="45">45 minutos</SelectItem>
                  <SelectItem value="60">60 minutos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Break Time */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Intervalo de Almoço</Label>
                <Switch 
                  checked={scheduleConfig.hasBreak} 
                  onCheckedChange={(v) => setScheduleConfig({...scheduleConfig, hasBreak: v})}
                />
              </div>
              {scheduleConfig.hasBreak && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Início</Label>
                    <Select value={scheduleConfig.breakStart} onValueChange={(v) => setScheduleConfig({...scheduleConfig, breakStart: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.slice(8, 16).map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Fim</Label>
                    <Select value={scheduleConfig.breakEnd} onValueChange={(v) => setScheduleConfig({...scheduleConfig, breakEnd: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.slice(10, 18).map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Work Days */}
            <div className="space-y-3">
              <Label>Dias de Atendimento</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { day: 1, label: 'Seg' },
                  { day: 2, label: 'Ter' },
                  { day: 3, label: 'Qua' },
                  { day: 4, label: 'Qui' },
                  { day: 5, label: 'Sex' },
                  { day: 6, label: 'Sáb' },
                  { day: 7, label: 'Dom' },
                ].map(({ day, label }) => (
                  <Button
                    key={day}
                    type="button"
                    variant={scheduleConfig.workDays.includes(day) ? 'default' : 'outline'}
                    size="sm"
                    className="w-12"
                    onClick={() => {
                      const newDays = scheduleConfig.workDays.includes(day)
                        ? scheduleConfig.workDays.filter(d => d !== day)
                        : [...scheduleConfig.workDays, day];
                      setScheduleConfig({...scheduleConfig, workDays: newDays});
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateWeekSchedule} className="bg-primary">
              <CheckCircle className="w-4 h-4 mr-2" />
              Aplicar na Semana
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Date Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquear Data</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Selecione a data para bloquear</Label>
            <Input 
              type="date" 
              className="mt-2"
              onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
            />
            <p className="text-sm text-slate-500 mt-2">
              Nenhuma consulta poderá ser agendada nesta data.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleBlockDate} variant="destructive">
              <Lock className="w-4 h-4 mr-2" />
              Bloquear Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
